import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Award, Download, Trash2, Search, UserPlus, Calendar, FileText,
  ChevronDown, Loader2, Upload, Eye, Check, X, FileUp, AlertCircle,
  Edit3, Bold, Italic, Type, Palette, RotateCcw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Preset colors for quick selection
const COLOR_PRESETS = [
  '#000000', '#1a1a1a', '#333333', '#666666',
  '#2B5EA7', '#1e40af', '#0ea5e9', '#0d9488',
  '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
  '#7c3aed', '#db2777', '#6b21a8', '#ffffff',
];

const FONT_SIZES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 20, 22, 24, 28, 32, 36, 42, 48];

// Text Field with Word-like Toolbar
const StyledTextField = ({ label, originalText, value, style, onChange, isRTL }) => {
  const currentText = value || '';
  const rawSize = style?.size || 12;
  // Round to nearest available size
  const currentSize = FONT_SIZES.reduce((prev, curr) => Math.abs(curr - rawSize) < Math.abs(prev - rawSize) ? curr : prev);
  const currentColor = style?.color || '#000000';
  const currentBold = style?.bold || false;
  const currentItalic = style?.italic || false;
  const isChanged = currentText !== originalText || style?.size || style?.color || style?.bold || style?.italic;

  const updateStyle = (key, val) => {
    onChange({ text: currentText, ...style, [key]: val });
  };

  const updateText = (newText) => {
    onChange({ text: newText, size: style?.size, color: style?.color, bold: style?.bold, italic: style?.italic });
  };

  return (
    <div className={`rounded-lg border ${isChanged ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 bg-gray-50/30'} overflow-hidden`}>
      {/* Label row */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        {isChanged && (
          <button onClick={() => onChange(originalText)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
            <RotateCcw className="h-3 w-3" />{isRTL ? 'إعادة' : 'Reset'}
          </button>
        )}
      </div>

      {/* Formatting Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 bg-white flex-wrap">
        {/* Font Size */}
        <div className="flex items-center gap-1">
          <Type className="h-3.5 w-3.5 text-gray-400" />
          <select
            value={currentSize}
            onChange={(e) => updateStyle('size', parseFloat(e.target.value))}
            className="h-7 text-xs border border-gray-200 rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="font-size-select"
          >
            {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Bold */}
        <button
          data-testid="bold-btn"
          onClick={() => updateStyle('bold', !currentBold)}
          className={`h-7 w-7 rounded flex items-center justify-center text-sm font-bold transition-colors ${currentBold ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'hover:bg-gray-100 text-gray-600 border border-transparent'}`}
          title="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </button>

        {/* Italic */}
        <button
          data-testid="italic-btn"
          onClick={() => updateStyle('italic', !currentItalic)}
          className={`h-7 w-7 rounded flex items-center justify-center text-sm transition-colors ${currentItalic ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'hover:bg-gray-100 text-gray-600 border border-transparent'}`}
          title="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </button>

        <div className="w-px h-5 bg-gray-200 mx-1" />

        {/* Color */}
        <div className="relative group">
          <button
            className="h-7 flex items-center gap-1 px-1.5 rounded hover:bg-gray-100 transition-colors"
            title={isRTL ? 'لون الخط' : 'Font Color'}
          >
            <Palette className="h-3.5 w-3.5 text-gray-500" />
            <div className="w-4 h-3 rounded-sm border border-gray-300" style={{ backgroundColor: currentColor }} />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-30 hidden group-hover:grid grid-cols-4 gap-1 w-36">
            {COLOR_PRESETS.map(c => (
              <button
                key={c}
                onClick={() => updateStyle('color', c)}
                className={`w-7 h-7 rounded border-2 transition-transform hover:scale-110 ${currentColor === c ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200'}`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
            <div className="col-span-4 mt-1 pt-1 border-t">
              <input
                type="color"
                value={currentColor}
                onChange={(e) => updateStyle('color', e.target.value)}
                className="w-full h-7 rounded cursor-pointer"
                title={isRTL ? 'لون مخصص' : 'Custom color'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Text Input */}
      <input
        type="text"
        value={currentText}
        onChange={(e) => updateText(e.target.value)}
        className="w-full px-3 py-2.5 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent"
        style={{
          fontWeight: currentBold ? 'bold' : 'normal',
          fontStyle: currentItalic ? 'italic' : 'normal',
          fontSize: `${Math.min(currentSize, 20)}px`,
          color: currentColor,
        }}
      />
    </div>
  );
};


const CertificateManagement = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [users, setUsers] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [certSearchTerm, setCertSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('issue');
  const [templateInfo, setTemplateInfo] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewCertId, setPreviewCertId] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [userName, setUserName] = useState('');
  // textEdits: { originalText: string | {text, size, color, bold, italic} }
  const [textEdits, setTextEdits] = useState({});
  const [autoDate, setAutoDate] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchUsers();
    fetchCertificates();
    fetchTemplateInfo();
    fetchAutoDate();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/teacher/users-for-certificates`, { headers });
      if (res.ok) setUsers(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/teacher/certificates`, { headers });
      if (res.ok) setCertificates(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchAutoDate = async () => {
    try {
      const res = await fetch(`${API}/api/teacher/certificates/auto-date`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAutoDate(data.date);
      }
    } catch (err) { console.error(err); }
  };

  const fetchTemplateInfo = async () => {
    try {
      const res = await fetch(`${API}/api/teacher/certificates/template-info`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTemplateInfo(data);
        if (data.text_blocks?.length) {
          const initial = {};
          data.text_blocks.forEach(b => { initial[b.text] = b.text; });
          setTextEdits(initial);
        }
      }
    } catch (err) { console.error(err); }
  };

  // Auto-fill date in textEdits when autoDate is fetched
  useEffect(() => {
    if (autoDate && templateInfo?.text_blocks) {
      const dateBlock = templateInfo.text_blocks.find(b => b.text.includes('Completed on'));
      if (dateBlock) {
        setTextEdits(prev => {
          const current = prev[dateBlock.text];
          // Only auto-fill if not already edited
          if (current === dateBlock.text || !current) {
            return { ...prev, [dateBlock.text]: `Completed on ${autoDate}` };
          }
          return prev;
        });
      }
    }
  }, [autoDate, templateInfo]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const t = searchTerm.toLowerCase();
    return users.filter(u => u.name?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t));
  }, [users, searchTerm]);

  const pendingCerts = certificates.filter(c => c.status === 'pending');
  const approvedCerts = certificates.filter(c => c.status === 'approved');

  const filteredApproved = useMemo(() => {
    if (!certSearchTerm) return approvedCerts;
    const t = certSearchTerm.toLowerCase();
    return approvedCerts.filter(c => c.user_name?.toLowerCase().includes(t) || c.user_email?.toLowerCase().includes(t));
  }, [approvedCerts, certSearchTerm]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserName(user.name);
    setSearchTerm(user.name);
    setShowDropdown(false);
    if (templateInfo?.text_blocks) {
      const nameBlock = templateInfo.text_blocks.find(b => b.text.includes('(') && b.text.includes(')'));
      if (nameBlock) {
        const currentEdit = textEdits[nameBlock.text];
        if (typeof currentEdit === 'object' && currentEdit !== null) {
          setTextEdits(prev => ({ ...prev, [nameBlock.text]: { ...currentEdit, text: user.name } }));
        } else {
          setTextEdits(prev => ({ ...prev, [nameBlock.text]: user.name }));
        }
      }
    }
  };

  const handleTextEditChange = useCallback((originalText, value) => {
    setTextEdits(prev => ({ ...prev, [originalText]: value }));
  }, []);

  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(isRTL ? 'PDF فقط' : 'PDF only'); return;
    }
    setUploadingTemplate(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/teacher/certificates/upload-template`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
      });
      if (res.ok) { toast.success(isRTL ? 'تم الرفع' : 'Uploaded'); fetchTemplateInfo(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setUploadingTemplate(false);
    e.target.value = '';
  };

  const handleGenerate = async () => {
    if (!selectedUser) { toast.error(isRTL ? 'اختر مستخدم' : 'Select user'); return; }
    if (!userName.trim()) { toast.error(isRTL ? 'أدخل الاسم' : 'Enter name'); return; }

    // Build edits: only changed fields
    const edits = {};
    for (const [original, edited] of Object.entries(textEdits)) {
      if (typeof edited === 'object' && edited !== null) {
        // Styled edit - always include
        if (edited.text !== original || edited.size || edited.color || edited.bold || edited.italic) {
          edits[original] = edited;
        }
      } else if (typeof edited === 'string' && edited !== original) {
        edits[original] = edited;
      }
    }

    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/teacher/certificates/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: selectedUser.id, user_name: userName, text_edits: edits })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isRTL ? 'تم الإنشاء - معاينة وموافقة' : 'Generated - preview & approve');
        fetchCertificates();
        setPreviewCertId(data.id);
        setActiveTab('pending');
        setSelectedUser(null); setUserName(''); setSearchTerm('');
        if (templateInfo?.text_blocks) {
          const init = {};
          templateInfo.text_blocks.forEach(b => { init[b.text] = b.text; });
          // Keep auto-date
          const dateBlock = templateInfo.text_blocks.find(b => b.text.includes('Completed on'));
          if (dateBlock && autoDate) init[dateBlock.text] = `Completed on ${autoDate}`;
          setTextEdits(init);
        }
      } else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setGenerating(false);
  };

  const handlePreview = async (certId) => {
    setPreviewCertId(certId);
    try {
      const res = await fetch(`${API}/api/certificates/${certId}/preview`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const blob = await res.blob(); setPreviewUrl(URL.createObjectURL(blob)); }
    } catch { toast.error('Error'); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null); setPreviewCertId(null);
  };

  const handleApprove = async (certId) => {
    try {
      const res = await fetch(`${API}/api/teacher/certificates/${certId}/approve`, { method: 'POST', headers });
      if (res.ok) { toast.success(isRTL ? 'تمت الموافقة' : 'Approved'); fetchCertificates(); closePreview(); }
    } catch { toast.error('Error'); }
  };

  const handleReject = async (certId) => {
    if (!window.confirm(isRTL ? 'رفض وحذف؟' : 'Reject & delete?')) return;
    try {
      const res = await fetch(`${API}/api/teacher/certificates/${certId}/reject`, { method: 'POST', headers });
      if (res.ok) { toast.success(isRTL ? 'تم الرفض' : 'Rejected'); fetchCertificates(); closePreview(); }
    } catch { toast.error('Error'); }
  };

  const handleDownload = async (certId, uName) => {
    try {
      const res = await fetch(`${API}/api/certificates/${certId}/download`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob(); const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `certificate_${uName.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      }
    } catch { toast.error('Error'); }
  };

  const handleDelete = async (certId) => {
    if (!window.confirm(isRTL ? 'حذف؟' : 'Delete?')) return;
    try {
      const res = await fetch(`${API}/api/teacher/certificates/${certId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success(isRTL ? 'تم الحذف' : 'Deleted'); fetchCertificates(); }
    } catch { toast.error('Error'); }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{isRTL ? 'معتمدة' : 'Approved'}</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{isRTL ? 'معلقة' : 'Pending'}</span>;
  };

  const getRoleBadge = (role) => {
    const labels = { teacher: isRTL ? 'مدرب' : 'Trainer', student: isRTL ? 'متدرب' : 'Trainee', lao: 'LAO' };
    const cls = { teacher: 'bg-blue-100 text-blue-700', student: 'bg-green-100 text-green-700', lao: 'bg-purple-100 text-purple-700' };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${cls[role] || 'bg-gray-100 text-gray-600'}`}>{labels[role] || role}</span>;
  };

  const getBlockLabel = (block) => {
    const t = block.text;
    if (t.includes('(') && t.includes(')')) return isRTL ? 'اسم المشارك' : 'Participant Name';
    if (t.includes('Completed on')) return isRTL ? 'تاريخ الإكمال (أوتوماتيك)' : 'Completion Date (auto)';
    if (t.includes('Certificate of Attendance')) return isRTL ? 'عنوان الشهادة' : 'Title';
    if (t.includes('Organizational Behavior')) return isRTL ? 'اسم البرنامج' : 'Program Name';
    if (t.includes('SHAMAH')) return isRTL ? 'اسم الأكاديمية (EN)' : 'Academy (EN)';
    if (block.size > 25) return isRTL ? 'عنوان' : 'Heading';
    return isRTL ? 'نص' : 'Text';
  };

  return (
    <div data-testid="certificate-management" className="space-y-6">
      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-lg">{isRTL ? 'معاينة الشهادة' : 'Preview'}</h3>
              <div className="flex gap-2">
                {previewCertId && certificates.find(c => c.id === previewCertId)?.status === 'pending' && (
                  <>
                    <Button data-testid="approve-preview-btn" onClick={() => handleApprove(previewCertId)} className="gap-1.5 bg-green-600 hover:bg-green-700" size="sm">
                      <Check className="h-4 w-4" />{isRTL ? 'موافقة' : 'Approve'}
                    </Button>
                    <Button data-testid="reject-preview-btn" onClick={() => handleReject(previewCertId)} variant="outline" size="sm" className="gap-1.5 text-red-600 hover:bg-red-50">
                      <X className="h-4 w-4" />{isRTL ? 'رفض' : 'Reject'}
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="sm" onClick={closePreview}><X className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-2 bg-gray-100">
              <object data={previewUrl} type="application/pdf" className="w-full h-[75vh] rounded-lg bg-white">
                <div className="flex flex-col items-center justify-center h-[75vh] gap-4">
                  <FileText className="h-16 w-16 text-gray-300" />
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    <Eye className="h-4 w-4" />{isRTL ? 'فتح' : 'Open'}
                  </a>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Award className="h-7 w-7 text-blue-600" />
          {isRTL ? 'إدارة الشهادات' : 'Certificates'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{isRTL ? 'إنشاء وإدارة شهادات الحضور' : 'Create and manage certificates'}</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        {[
          { key: 'template', icon: FileUp, label: isRTL ? 'النموذج' : 'Template' },
          { key: 'issue', icon: Edit3, label: isRTL ? 'إصدار شهادة' : 'Issue' },
          { key: 'pending', icon: AlertCircle, label: isRTL ? 'معلقة' : 'Pending', count: pendingCerts.length },
          { key: 'approved', icon: Check, label: isRTL ? 'معتمدة' : 'Approved', count: approvedCerts.length },
        ].map(tab => (
          <Button key={tab.key} data-testid={`tab-${tab.key}`} variant={activeTab === tab.key ? 'default' : 'outline'} onClick={() => setActiveTab(tab.key)} className="gap-2" size="sm">
            <tab.icon className="h-4 w-4" />{tab.label}
            {tab.count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20' : 'bg-blue-100 text-blue-700'}`}>{tab.count}</span>}
          </Button>
        ))}
      </div>

      {/* TEMPLATE TAB */}
      {activeTab === 'template' && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-3">{isRTL ? 'نموذج الشهادة' : 'Template'}</h2>
          <div className={`flex items-center gap-3 p-4 rounded-lg ${templateInfo?.has_template ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
            {templateInfo?.has_template ? (
              <><Check className="h-5 w-5 text-green-600" /><div><p className="font-medium text-green-800">{isRTL ? 'النموذج مرفوع' : 'Template uploaded'}</p>{templateInfo.filename && <p className="text-sm text-green-600">{templateInfo.filename}</p>}<p className="text-xs text-green-500 mt-1">{templateInfo.text_blocks?.length || 0} {isRTL ? 'حقل نص' : 'text fields'}</p></div></>
            ) : (
              <><AlertCircle className="h-5 w-5 text-amber-600" /><p className="font-medium text-amber-800">{isRTL ? 'لم يتم رفع نموذج' : 'No template'}</p></>
            )}
          </div>
          <div className="flex gap-3">
            <label className="cursor-pointer">
              <input type="file" accept=".pdf" onChange={handleTemplateUpload} className="hidden" />
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                {uploadingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {isRTL ? 'رفع PDF' : 'Upload PDF'}
              </div>
            </label>
            {templateInfo?.has_template && (
              <Button variant="outline" onClick={() => {
                fetch(`${API}/api/teacher/certificates/preview-template`, { headers: { 'Authorization': `Bearer ${token}` } })
                  .then(r => r.blob()).then(b => { setPreviewUrl(URL.createObjectURL(b)); setPreviewCertId(null); });
              }} className="gap-2" size="sm">
                <Eye className="h-4 w-4" />{isRTL ? 'معاينة' : 'Preview'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ISSUE TAB */}
      {activeTab === 'issue' && (
        <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b pb-3 flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-blue-600" />
            {isRTL ? 'إصدار شهادة جديدة' : 'Issue New Certificate'}
          </h2>

          {!templateInfo?.has_template && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertCircle className="h-4 w-4" />{isRTL ? 'ارفع النموذج أولاً' : 'Upload template first'}
            </div>
          )}

          {/* User Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{isRTL ? 'اختر المستخدم' : 'Select User'}</label>
            <div className="relative">
              <div className="relative">
                <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input data-testid="user-search-input" value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); if (selectedUser && e.target.value !== selectedUser.name) setSelectedUser(null); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={isRTL ? 'ابحث...' : 'Search...'} className={isRTL ? 'pr-10' : 'pl-10'} />
                <ChevronDown className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'left-3' : 'right-3'}`} />
              </div>
              {showDropdown && filteredUsers.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredUsers.map(user => (
                    <button key={user.id} data-testid={`user-option-${user.id}`} onClick={() => handleSelectUser(user)}
                      className={`w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center justify-between ${selectedUser?.id === user.id ? 'bg-blue-50' : ''}`}>
                      <div><div className="font-medium text-gray-800">{user.name}</div><div className="text-xs text-gray-500">{user.email}</div></div>
                      {getRoleBadge(user.role)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedUser && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-lg">
                <Award className="h-4 w-4" />{isRTL ? 'تم اختيار:' : 'Selected:'} <strong>{selectedUser.name}</strong>
              </div>
            )}
          </div>

          {/* Name field WITH styling toolbar */}
          {(() => {
            const nameBlock = templateInfo?.text_blocks?.find(b => b.text.includes('(') && b.text.includes(')'));
            const nameOriginal = nameBlock?.text || '';
            const nameEdit = textEdits[nameOriginal];
            let nameStyle;
            if (typeof nameEdit === 'object' && nameEdit !== null) {
              nameStyle = nameEdit;
            } else {
              nameStyle = { size: nameBlock?.size || 17, color: nameBlock?.color || '#000000', bold: true, italic: false };
            }
            return (
              <StyledTextField
                label={isRTL ? 'اسم المشارك في الشهادة' : 'Participant Name on Certificate'}
                originalText={nameOriginal}
                value={userName}
                style={nameStyle}
                onChange={(val) => {
                  if (typeof val === 'string') {
                    setUserName(val);
                    if (nameOriginal) handleTextEditChange(nameOriginal, val);
                  } else {
                    setUserName(val.text || '');
                    if (nameOriginal) handleTextEditChange(nameOriginal, val);
                  }
                }}
                isRTL={isRTL}
              />
            );
          })()}

          {/* Date field with calendar picker */}
          {(() => {
            const dateBlock = templateInfo?.text_blocks?.find(b => b.text.includes('Completed on'));
            const dateOriginal = dateBlock?.text || '';
            const dateEdit = textEdits[dateOriginal];
            let dateDisplayText = '';
            if (typeof dateEdit === 'object' && dateEdit !== null) {
              dateDisplayText = dateEdit.text || '';
            } else {
              dateDisplayText = dateEdit || dateOriginal;
            }
            // Extract just the date part after "Completed on "
            const dateOnly = dateDisplayText.replace(/^Completed on\s*/i, '');

            return (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  {isRTL ? 'تاريخ الإكمال (أوتوماتيك)' : 'Completion Date (auto)'}
                </label>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Completed on</span>
                  <Input
                    data-testid="certificate-date-picker"
                    type="date"
                    value={(() => {
                      // Try to parse existing date to yyyy-mm-dd for the input
                      try {
                        const d = new Date(dateOnly);
                        if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
                      } catch {}
                      return '';
                    })()}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) {
                        const d = new Date(val + 'T00:00:00');
                        const formatted = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
                        const fullText = `Completed on ${formatted}`;
                        if (dateOriginal) {
                          const currentEdit = textEdits[dateOriginal];
                          if (typeof currentEdit === 'object' && currentEdit !== null) {
                            handleTextEditChange(dateOriginal, { ...currentEdit, text: fullText });
                          } else {
                            handleTextEditChange(dateOriginal, fullText);
                          }
                        }
                      }
                    }}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  {isRTL ? 'سيظهر في الشهادة:' : 'Will show as:'} <strong>{dateDisplayText || '...'}</strong>
                </p>
              </div>
            );
          })()}

          {/* Editable text fields with formatting */}
          {templateInfo?.text_blocks?.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 border-t pt-4">
                <Type className="h-4 w-4 text-blue-600" />
                {isRTL ? 'نصوص الشهادة - تعديل مع التنسيق' : 'Certificate Text - Edit with Formatting'}
              </div>

              {templateInfo.text_blocks.map((block, idx) => {
                const originalText = block.text;
                const isName = originalText.includes('(') && originalText.includes(')');
                const isDate = originalText.includes('Completed on');
                if (isName || isDate) return null; // handled separately above

                const currentEdit = textEdits[originalText];
                let displayText, displayStyle;

                if (typeof currentEdit === 'object' && currentEdit !== null) {
                  displayText = currentEdit.text || '';
                  displayStyle = currentEdit;
                } else {
                  displayText = currentEdit || originalText;
                  displayStyle = {
                    size: block.size,
                    color: block.color,
                    bold: block.bold,
                    italic: block.italic,
                  };
                }

                return (
                  <StyledTextField
                    key={idx}
                    label={`${getBlockLabel(block)} (${block.size}pt)`}
                    originalText={originalText}
                    value={displayText}
                    style={displayStyle}
                    onChange={(val) => handleTextEditChange(originalText, val)}
                    isRTL={isRTL}
                  />
                );
              })}
            </div>
          )}

          {/* Generate Button */}
          <div className="flex justify-end pt-4">
            <Button data-testid="generate-certificate-btn" onClick={handleGenerate}
              disabled={generating || !selectedUser || !userName}
              className="gap-2 bg-blue-600 hover:bg-blue-700" size="lg">
              {generating ? <><Loader2 className="h-4 w-4 animate-spin" />{isRTL ? 'جاري...' : 'Generating...'}</> :
                <><Award className="h-4 w-4" />{isRTL ? 'إنشاء الشهادة' : 'Generate'}</>}
            </Button>
          </div>
        </div>
      )}

      {/* PENDING TAB */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{isRTL ? 'معلقة' : 'Pending'} ({pendingCerts.length})</h2>
          {pendingCerts.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center"><Check className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{isRTL ? 'لا توجد معلقة' : 'None'}</p></div>
          ) : (
            <div className="grid gap-4">{pendingCerts.map(c => <CertCard key={c.id} cert={c} isRTL={isRTL} onPreview={handlePreview} onApprove={handleApprove} onReject={handleReject} onDownload={handleDownload} onDelete={handleDelete} showApproval getStatusBadge={getStatusBadge} />)}</div>
          )}
        </div>
      )}

      {/* APPROVED TAB */}
      {activeTab === 'approved' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold">{isRTL ? 'معتمدة' : 'Approved'} ({approvedCerts.length})</h2>
            <div className="relative flex-1 max-w-xs">
              <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
              <Input data-testid="cert-search-input" value={certSearchTerm} onChange={(e) => setCertSearchTerm(e.target.value)} placeholder={isRTL ? 'بحث...' : 'Search...'} className={`${isRTL ? 'pr-10' : 'pl-10'} h-9`} />
            </div>
          </div>
          {filteredApproved.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center"><Award className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">{isRTL ? 'لا توجد' : 'None'}</p></div>
          ) : (
            <div className="grid gap-4">{filteredApproved.map(c => <CertCard key={c.id} cert={c} isRTL={isRTL} onPreview={handlePreview} onDownload={handleDownload} onDelete={handleDelete} getStatusBadge={getStatusBadge} />)}</div>
          )}
        </div>
      )}
    </div>
  );
};

const CertCard = ({ cert, isRTL, onPreview, onApprove, onReject, onDownload, onDelete, showApproval, getStatusBadge }) => (
  <div data-testid={`certificate-card-${cert.id}`} className="bg-white rounded-xl border shadow-sm p-5 hover:shadow-md transition-shadow">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0"><Award className="h-6 w-6 text-blue-600" /></div>
        <div>
          <div className="flex items-center gap-2"><h3 className="font-semibold text-gray-900">{cert.user_name}</h3>{getStatusBadge(cert.status)}</div>
          <p className="text-sm text-gray-500">{cert.user_email}</p>
          <p className="text-xs text-gray-400 mt-1">{isRTL ? 'صادرة:' : 'By:'} {cert.issued_by_name} &middot; {new Date(cert.created_at).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0 flex-wrap">
        <Button data-testid={`preview-cert-${cert.id}`} variant="outline" size="sm" onClick={() => onPreview(cert.id)} className="gap-1.5"><Eye className="h-4 w-4" />{isRTL ? 'معاينة' : 'Preview'}</Button>
        {showApproval && onApprove && (
          <>
            <Button data-testid={`approve-cert-${cert.id}`} size="sm" onClick={() => onApprove(cert.id)} className="gap-1.5 bg-green-600 hover:bg-green-700"><Check className="h-4 w-4" />{isRTL ? 'موافقة' : 'Approve'}</Button>
            <Button data-testid={`reject-cert-${cert.id}`} variant="outline" size="sm" onClick={() => onReject(cert.id)} className="gap-1.5 text-red-600 hover:bg-red-50"><X className="h-4 w-4" />{isRTL ? 'رفض' : 'Reject'}</Button>
          </>
        )}
        {cert.status === 'approved' && (
          <Button data-testid={`download-cert-${cert.id}`} variant="outline" size="sm" onClick={() => onDownload(cert.id, cert.user_name)} className="gap-1.5"><Download className="h-4 w-4" />{isRTL ? 'تحميل' : 'Download'}</Button>
        )}
        <Button data-testid={`delete-cert-${cert.id}`} variant="outline" size="sm" onClick={() => onDelete(cert.id)} className="gap-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  </div>
);

export default CertificateManagement;
